import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import cookies from 'js-cookie';
import { baseUrls, GlobalInterfaces, cookieNames } from '@monorepo/common';
import { showNotification } from '../features/sliceNotification';

const token: string | null = cookies.get(cookieNames.bevnet_token) || null;

const baseQuery = fetchBaseQuery({
  baseUrl: baseUrls.jobApi,
  ...(!!token) && {
    prepareHeaders: (headers) => {
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    }
  }
});

interface IOldErrorModel {
  data?: {
    error?: string
  },
  status?: number
}
const errorStatus: number[] = [400, 401, 403];

export interface IBaseQueryExtraOptions {
  suppressErrorNotification?: boolean;
}

export const baseQueryWithReAuth: BaseQueryFn<
string | FetchArgs,
unknown,
FetchBaseQueryError,
IBaseQueryExtraOptions
> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  // Public job-management access guard (T2): 401 = anonymous → send to login and
  // return here once they re-authenticate. 403 = access withdrawn → pass through
  // so the pages can render the "access removed" message (never redirect on 403).
  if (result?.error && result.error.status === 401) {
    window.location.href = `${baseUrls.login}?returnUrl=${encodeURIComponent(window.location.href)}`;
    return result;
  }

  // A few user actions render precise, contextual errors beside the control that
  // failed. Let those endpoint definitions opt out of the generic global toast so
  // the user receives one message rather than two competing notifications.
  if (result?.error && extraOptions?.suppressErrorNotification) {
    return result;
  }

  if (result?.error && (!errorStatus.includes(result.error.status as number))) {
    if ('data' in result.error) {
      const err = result.error as IOldErrorModel;
      if (err.data?.error) {
        api.dispatch(showNotification({
          type: 'error',
          show: true,
          text: err.data?.error
        }));
      }
    }
  }

  if (result.error && result.error.status === 400) {
    // The 400 body shape varies by endpoint:
    //   - { errors: { Field: string[] } } — model-validation failures (join the messages)
    //   - a plain string, e.g. "Captcha is invalid" — captcha/manual BadRequest(string)
    //   - anything else — fall back to a generic message.
    // Never throw here: a thrown baseQuery swallows the toast and breaks the UI.
    const data = (result.error as { data?: unknown }).data;
    let errText = '';

    if (data && typeof data === 'object' && 'errors' in (data as Record<string, unknown>)) {
      const { errors } = data as { errors: Record<string, unknown> };
      if (errors && typeof errors === 'object') {
        Object.keys(errors).forEach((item) => {
          errText += `${errors[item]} \n`;
        });
      }
    } else if (typeof data === 'string' && data.trim()) {
      errText = data;
    }

    api.dispatch(showNotification({
      type: 'error',
      show: true,
      text: errText.trim() || 'Something went wrong. Please try again.'
    }));
  }

  if (result?.error && ('originalStatus' in result.error)) {
    const err = result.error as GlobalInterfaces.IErrorQueryProps;
    if (err?.originalStatus === 500) {
      api.dispatch(showNotification({
        show: true,
        type: 'error',
        text: 'Internal server Error!'
      }));
    }
    if (err?.originalStatus === 400) {
      api.dispatch(showNotification({
        type: 'error',
        show: true,
        text: err.data
      }));
    }
  }

  return result;
};
